import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  Paper,
  Snackbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Professional, Team } from "@syadem/ariane-js";
import { VaccinationAct as ProVaccinationAct } from "@syadem/kairos-pro-js";
import { VaccinationAct as TeamVaccinationAct, VaccinationActCertificator } from "@syadem/kairos-team-js";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useHealthRecord } from "../../../store";
import { DownloadCertificateButton } from "../../components/DownloadCertificateButton";
import { IconMedicalInstrumentSyringe } from "../../components/icons/IconMedicalInstrumentSyringe";
import { EmptyListPlaceholder } from "../../components/shared/EmptyListPlaceholder";
import { VaccinationActForm } from "../../components/VaccinationActForm";
import { EvcImportDialog } from "../../components/evc/EvcImportDialog";
import { VaccinationActListSort } from "../../components/VaccinationActListSort";
import VaccinationActsByDiseaseGroups from "../../components/VaccinationActsByDiseaseGroups";
import VaccinationActsByList from "../../components/VaccinationActsByList";
import { useArianeApi, useDaphne } from "../../hooks";
import { useFetchHealthRecord } from "../../hooks/useFetchHealthRecord";
import { useI18n } from "../../hooks/useI18n";
import { theme } from "../../layout/Theme";

export function VaccinationActs() {
  const { t } = useI18n();
  const { id } = useParams() as { id: string };
  const [searchParams] = useSearchParams();
  const daphne = useDaphne();
  const arianeApi = useArianeApi();

  const { isLoading, error, refetch } = useFetchHealthRecord(id);
  const healthRecord = useHealthRecord(id);

  const [snackOpen, setSnackOpen] = useState<boolean>(searchParams.get("updated") == "true");
  const [certificators, setCertificators] = useState<(Professional | Team)[] | undefined>(undefined);
  const [listType, setListType] = useState("byDiseases");
  const [drawerFormOpen, setDrawerFormOpen] = useState(false);
  const [sortedVaccinationActs, setSortedVaccinationActs] = useState<ProVaccinationAct[] | TeamVaccinationAct[]>([]);
  const breakpointSm = useMediaQuery(theme.breakpoints.down("md"));
  const [openDatamatrix, setOpenDatamatrix] = useState(false);
  const [evcDialogOpen, setEvcDialogOpen] = useState(false);
  const enableEvcImport = import.meta.env.VITE_ENABLE_EVC_IMPORT === "true";

  const drawerWidth = breakpointSm ? "100%" : "50%";

  useEffect(() => {
    (async () => {
      if (healthRecord?.vaccinationActs) {
        const certificatorIds = healthRecord?.vaccinationActs
          .filter((act) => act.certificator !== null)
          .flatMap((act) => {
            const ids = [];
            const certificator = act.certificator as VaccinationActCertificator;
            ids.push(certificator.id);
            if (certificator.teamMemberAccountId) {
              ids.push(certificator.teamMemberAccountId);
            }
            return ids;
          });

        let actCertificators: (Professional | Team)[] = [];

        if (certificatorIds.length > 0) {
          try {
            actCertificators = await arianeApi.professionals.searchByIds([...new Set(certificatorIds)]);
          } catch (_error) {
            actCertificators = [];
          }
        }

        setCertificators(actCertificators);
      }
    })();
  }, [arianeApi?.professionals, healthRecord?.vaccinationActs]);

  const closeDrawer = () => {
    setDrawerFormOpen(false);
    setOpenDatamatrix(false);
  };

  return (
    <>
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackOpen(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ ml: "150px" }}
      >
        <Alert severity="success">{t("common.alerts.alert_saved_success")}</Alert>
      </Snackbar>

      <Paper sx={{ overflow: "hidden", backgroundColor: "inherit" }} elevation={0}>
        {error && (
          <Typography sx={{ my: 4 }} align="center" component="div">
            <Alert severity="error">{t("common.alerts.alert_notification")}</Alert>
          </Typography>
        )}
        {isLoading ? (
          <LinearProgress variant="query" sx={{ my: 2 }} />
        ) : healthRecord && daphne && healthRecord.vaccinationActs.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                mt: 1,
              }}
            >
              <Box
                sx={{
                  width: { xs: "100%", md: "auto" },
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  mt: { xs: 1, md: 0 },
                  gap: 1,
                }}
              >
                <VaccinationActListSort
                  vaccinationActs={healthRecord.vaccinationActs}
                  setSortedVaccinationActs={setSortedVaccinationActs}
                  daphne={daphne}
                  setListType={setListType}
                />
                <DownloadCertificateButton healthRecord={healthRecord} />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  size={breakpointSm ? "small" : "medium"}
                  onClick={() => setDrawerFormOpen(true)}
                  startIcon={<AddIcon />}
                  disableElevation
                  variant="contained"
                >
                  {t("vaccines.add_vaccination")}
                </Button>
                {enableEvcImport && (
                  <Button
                    variant="outlined"
                    onClick={() => setEvcDialogOpen(true)}
                    startIcon={<QrCodeScannerIcon />}
                  >
                    {t("vaccines.scan_evc")}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={() => {
                    setDrawerFormOpen(true);
                    setOpenDatamatrix(true);
                  }}
                  startIcon={<SearchIcon />}
                >
                  Datamatrix
                </Button>
              </Box>
            </Box>
            <Typography variant="body2" color="rgba(0, 0, 0, 0.6)">
              {t("vaccines.count", {
                smart_count: sortedVaccinationActs?.length,
              })}
            </Typography>

            <List sx={{ width: "100%" }}>
              {listType === "byList" && (
                <VaccinationActsByList
                  daphne={daphne}
                  certificators={certificators || []}
                  healthRecord={healthRecord}
                  sortedVaccinationActs={sortedVaccinationActs}
                />
              )}
              {listType === "byDiseases" && (
                <VaccinationActsByDiseaseGroups
                  vaccinationActs={healthRecord.vaccinationActs}
                  daphne={daphne}
                  certificators={certificators}
                  healthRecord={healthRecord}
                />
              )}
            </List>
          </>
        ) : null}
        <Drawer
          anchor="right"
          open={drawerFormOpen}
          onClose={closeDrawer}
          sx={{ width: drawerWidth, "& .MuiDrawer-paper": { width: drawerWidth } }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <IconButton onClick={closeDrawer} sx={{ ml: 2 }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
          <Divider />
          <Paper sx={{ border: "none", px: 4, py: 2 }} elevation={0}>
            {healthRecord ? (
              <VaccinationActForm
                isEmbedded
                healthRecord={healthRecord}
                setSnackOpen={setSnackOpen}
                handleClose={closeDrawer}
                isDatamatrixOpen={openDatamatrix}
              />
            ) : (
              <LinearProgress variant="query" />
            )}
          </Paper>
        </Drawer>
        {!isLoading && healthRecord && healthRecord.vaccinationActs.length === 0 ? (
          <EmptyListPlaceholder
            title={t("vaccines.no_vaccinations")}
            icon={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.primary[500],
                  ".iconSyringe": {
                    height: "35px !important",
                  },
                }}
              >
                <IconMedicalInstrumentSyringe />
              </Box>
            }
            button={
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="large"
                  onClick={() => setDrawerFormOpen(true)}
                  variant="contained"
                  startIcon={<AddIcon />}
                  disableElevation
                >
                  {t("vaccines.add_vaccination")}
                </Button>
                {enableEvcImport && (
                  <Button
                    size="large"
                    variant="outlined"
                    onClick={() => setEvcDialogOpen(true)}
                    startIcon={<QrCodeScannerIcon />}
                  >
                    {t("vaccines.scan_evc")}
                  </Button>
                )}
              </Box>
            }
          >
            {t("vaccines.add_vaccination_msg")}
          </EmptyListPlaceholder>
        ) : null}
        {enableEvcImport && (
          <EvcImportDialog
            open={evcDialogOpen}
            onClose={() => setEvcDialogOpen(false)}
            healthRecordVaccinations={healthRecord?.vaccinationActs}
            healthRecordId={healthRecord?.id}
            healthRecordName={healthRecord ? `${healthRecord.firstNames} ${healthRecord.lastName}` : undefined}
            healthRecordBirthDate={healthRecord?.birthDate?.toISOString()}
            onImportComplete={refetch}
          />
        )}
      </Paper>
    </>
  );
}
